// Imports
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled, { css } from 'styled-components/macro';
import { useAppDispatch, useAppSelector } from '../../../../../../../shareables/foundation/front-end/redux/hooks';
import apiActions from '../../../../../../../shareables/integrations/api/redux/actions';
import { GetYOTMResponse } from '../../../../../../../shareables/types/api/main/website/homepage/yotm/get';


// Styled components
const Container = styled.div`
	width: 100%;
	margin-top: 0.9375rem;
	padding: 5%;
	box-shadow: 0 0 0.3125rem rgba(0, 0, 0, 0.2);
	font-size: 0.8125rem;
	line-height: 1rem;
	text-align: left;
	color: #555;
	border-radius: 0.1875rem;
	
	h3 {
		padding: 0 0 0.125rem 0;
		margin: 0 0 0.625rem 0;
		font-size: 1.0625rem;
		font-weight: bold;
		color: #222;
		line-height: 1.1;
	}
`;

const YardOfTheMonthImage = styled.img`
	width: 100%;
`;

const YardOfTheMonthSkeletonContainer = styled.div`
	width: 100%;
	padding-top: 70%;
	position: relative;
	margin-bottom: 0.625rem;
`;

const YardOfTheMonthSkeleton = styled(Skeleton)`
	position: absolute;
	top: 0;
	left: 0;
	
	width: 100%;
	height: 100%;
`;

const Caption = styled.figcaption<{ moreSpace: boolean }>`
	overflow-wrap: anywhere;
	
	${({ moreSpace }) =>
		moreSpace &&
		css`
			padding-top: 0.4063rem;
		`}
`;


// Define the accepted props
interface Props {
	storybookYardOfTheMonth?: GetYOTMResponse;
}


// Function component
const YardOfTheMonth: React.FC<Props> = ({ storybookYardOfTheMonth }) => {
	// Use state
	const [yardOfTheMonth, setYardOfTheMonth] = useState<GetYOTMResponse | null>(storybookYardOfTheMonth ?? null);
	const [isImageLoading, setIsImageLoading] = useState<boolean>(true);
	
	
	// Use Redux functionality
	const hasPhoto = useAppSelector((state) => state.self.homepageVisibility?.yotm.photo);
	const dispatch = useAppDispatch();
	
	
	// Load yard of the month on mount
	useEffect(() => {
		void dispatch(
			apiActions.call<undefined, undefined, GetYOTMResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/homepage/yotm`,
				completion: (json) => {
					setYardOfTheMonth(json.response);
				},
			})
		);
	}, [dispatch]);
	
	
	// If loading, then display nothing
	if (yardOfTheMonth === null) {
		return null;
	}
	
	
	// Build default description
	const defaultDescription = `Yard of the month for “${new Date(Date.now()).toLocaleDateString(undefined, {
		month: 'long',
	})}”`;
	
	
	// Return JSX
	return (
		<Container>
			<h3>Yard of the Month</h3>
			<figure>
				{yardOfTheMonth.photo && (
					<YardOfTheMonthImage
						alt={yardOfTheMonth.description ?? defaultDescription}
						src={yardOfTheMonth.photo}
						onLoad={() => setIsImageLoading(false)}
						hidden={isImageLoading}
					/>
				)}
				
				{hasPhoto && isImageLoading && (
					<YardOfTheMonthSkeletonContainer>
						<YardOfTheMonthSkeleton />
					</YardOfTheMonthSkeletonContainer>
				)}
				
				{yardOfTheMonth.description && (
					<Caption moreSpace={Boolean(yardOfTheMonth.photo)}>{yardOfTheMonth.description}</Caption>
				)}
			</figure>
		</Container>
	);
};

export default YardOfTheMonth;
